import { useContext, useEffect, useRef, useState } from 'react';
import PropType from 'prop-types';

import { useCanAccessNavItem } from 'hooks/useCanAccessNavItem';
import { HeadquarterContext } from 'context/HeadquarterContext';

import { Box } from '@mantine/core';

import { ErrorBoundaryUnauthorizedError } from 'errorBoundary/errors';
import Cover from 'primitives/Cover/Cover';
import MainContentLoader from 'primitives/MainContentLoader/MainContentLoader';

import { sanitizeAdminUrl } from './helpers';

function EmbeddedAdminFrame({ src, title, navKey }) {
  const adminUrl = import.meta.env.VITE_ADMIN_PYTHON_APP_URL;
  const [isLoading, setIsLoading] = useState(true);
  const canAccessNavItem = useCanAccessNavItem();
  const { refreshCompanyLocations, silentRefreshLocation } = useContext(HeadquarterContext);
  const refreshRef = useRef(refreshCompanyLocations);
  const refreshLocationRef = useRef(silentRefreshLocation);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    refreshRef.current = refreshCompanyLocations;
    refreshLocationRef.current = silentRefreshLocation;
  }, [refreshCompanyLocations, silentRefreshLocation]);

  useEffect(() => {
    setIsLoading(true);

    const handleMessage = e => {
      const iframeLocation = e?.data?.cn_location;

      if (iframeLocation) {
        setIsLoading(false);
        void refreshRef.current();
        void refreshLocationRef.current();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const iframeSrcUrl = new URL(`${sanitizeAdminUrl(adminUrl ?? '')}/${src}`);
  iframeSrcUrl.searchParams.set('one-dash', 'true');

  if (!canAccessNavItem(navKey)) {
    throw new ErrorBoundaryUnauthorizedError(title);
  }

  return (
    <>
      {title && <Cover title={title} />}
      <Box
        style={{
          height: '1000px',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {isLoading && (
          <Box pos="absolute" w="100%">
            <MainContentLoader />
          </Box>
        )}
        <iframe
          src={iframeSrcUrl.toString()}
          onLoad={() => {
            if (!isInitialLoad.current) {
              void refreshRef.current();
              void refreshLocationRef.current();
            }
            isInitialLoad.current = false;
          }}
          style={{
            border: 0,
            height: '100%',
            width: '100%',
            borderRadius: 'var(--mantine-radius-sm)',
            position: 'relative',
          }}
          title="admin"
        />
      </Box>
    </>
  );
}

EmbeddedAdminFrame.propTypes = {
  src: PropType.string.isRequired,
  title: PropType.string,
  navKey: PropType.string,
};

// keyed on src so switching between admin tabs remounts the iframe
export default function EmbeddedAdmin(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
  return <EmbeddedAdminFrame key={props.src} {...props} />;
}
